import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { initializeExchangeRates } from '../utils/currency';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Load from localStorage or default to INR
    try {
      const saved = localStorage.getItem('selected_currency');
      return saved && ['INR', 'USD', 'EUR'].includes(saved) ? saved : 'INR';
    } catch {
      return 'INR';
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize exchange rates on mount
  useEffect(() => {
    const init = async () => {
      try {
        await initializeExchangeRates();
        setIsInitialized(true);
      } catch (error) {
        console.warn('Failed to initialize exchange rates:', error);
        setIsInitialized(true); // Still mark as initialized to allow app to function
      }
    };
    init();
  }, []);

  // Save currency to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('selected_currency', currency);
    } catch (error) {
      console.warn('Failed to save currency preference:', error);
    }
  }, [currency]);

  const changeCurrency = useCallback((newCurrency) => {
    if (['INR', 'USD', 'EUR'].includes(newCurrency)) {
      setCurrency(newCurrency);
    }
  }, []);

  const value = {
    currency,
    changeCurrency,
    isInitialized,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
